import { FormikProvider, useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import FormError from "../../Tags/FormError";
import { addMembersToTeam } from "../../../apis/workspace/teams";
import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import Image from "../../Tags/Image";
import { Button } from "../../ui/button";
import VirtualUserMultiSelect from "../shared/VirtualUserMultiSelect";
import { useCheckTeamMembersConflicts } from "../../../apiHooks/useCheckTeamMembersConflicts";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

const areArraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export default function AddMembersModal({
  closeModal,
  data = {},
  teamMembers = [],
  activeWorkspace,
  refetch = () => undefined,
}) {
  const captureEvent = useCaptureEvent();
  const queryClient = useQueryClient();
  const workspaceId = activeWorkspace?.id;

  const getIds = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => item && item.id).filter(Boolean);
  };

  const existingMemberIds = useMemo(() => getIds(data?.members), [data?.members]);
  const availableMembers = useMemo(() => {
    if (!Array.isArray(teamMembers)) return [];
    return teamMembers.filter((member) => member && !existingMemberIds.includes(member.id));
  }, [teamMembers, existingMemberIds]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [conflictMembers, setConflictMembers] = useState({});
  const [hasConflicts, setHasConflicts] = useState(false);
  const [disabledMemberIds, setDisabledMemberIds] = useState(existingMemberIds);
  const conflictCheckRequestId = useRef(0);

  const { mutateAsync: checkTeamMemberConflicts, isLoading: isCheckingTeamMemberConflicts } =
    useCheckTeamMembersConflicts({
      onError: (error) => {
        const message = error?.response?.data?.message ?? "Failed to check member conflicts.";
        toast.error(message);
      },
    });

  const runConflictCheck = async (memberIds = []) => {
    const safeMemberIds = Array.isArray(memberIds) ? memberIds : [];
    conflictCheckRequestId.current += 1;
    const requestId = conflictCheckRequestId.current;

    if (!workspaceId || !data?.id || safeMemberIds.length === 0) {
      setConflictMembers({});
      setHasConflicts(false);
      return;
    }

    try {
      const response = await checkTeamMemberConflicts({
        workspaceId,
        teamId: data?.id,
        teamMembers: safeMemberIds,
      });

      if (conflictCheckRequestId.current !== requestId) {
        return;
      }

      const responseData = response?.data?.data || response?.data || {};
      const conflicts = responseData?.conflicts || [];

      const newWarnings = {};
      const newErrors = []; // IDs of users already in current team

      if (Array.isArray(conflicts)) {
        conflicts.forEach((conflict) => {
          const user = conflict?.user || conflict?.member || {};
          const userId = user.id;
          const currentTeam = conflict?.current_team || conflict?.team || {};
          
          if (userId) {
            if (currentTeam.id === data.id) {
               // User is already in THIS team
               newErrors.push(userId);
            } else {
               // User is in ANOTHER team
               newWarnings[userId] = currentTeam.name || "another team";
            }
          }
        });
      }

      // Handle current team conflicts (Errors)
      if (newErrors.length > 0) {
        // Remove them from selection
        setSelectedIds(prev => prev.filter(id => !newErrors.includes(id)));
        // Add them to disabled list
        setDisabledMemberIds(prev => {
           const next = new Set([...prev, ...newErrors]);
           return Array.from(next);
        });
        toast.error(`Removed ${newErrors.length} user(s) who are already in this team.`);
      }

      setConflictMembers(newWarnings);
      setHasConflicts(Object.keys(newWarnings).length > 0);
    } catch (error) {
      if (conflictCheckRequestId.current === requestId) {
        setConflictMembers({});
        setHasConflicts(false);
      }
    }
  };

  useEffect(() => {
    setSelectedIds([]);
    setConflictMembers({});
    setHasConflicts(false);
    conflictCheckRequestId.current += 1;
  }, [data?.id]);

  const formInitialValues = useMemo(() => ({ team_members: [] }), []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        team_members: Yup.array()
          .of(Yup.string().required())
          .min(1, "Select at least one member")
          .required("Select at least one member"),
      }),
    []
  );

  const handleModalClose = () => {
    formik.resetForm();
    setSelectedIds([]);
    setConflictMembers({});
    setHasConflicts(false);
    conflictCheckRequestId.current += 1;
    closeModal();
  };

  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = {
        team_members: values.team_members.map(userId => ({
          user_id: userId,
          role: "member"
        }))
      };
      addMembersToTeamFn.mutate({
        data: payload,
        teamId: data?.id,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      });
    },
  });

  useEffect(() => {
    const current = formik.values?.team_members || [];
    if (!areArraysEqual(current, selectedIds)) {
      formik.setFieldValue("team_members", selectedIds, true);
    }
  }, [formik, selectedIds]);

  const handleMembersChange = (ids = []) => {
    if (!Array.isArray(ids)) return;
    const uniqueIds = [...new Set(ids)];
    setSelectedIds(uniqueIds);
    runConflictCheck(uniqueIds);
  };

  const addMembersToTeamFn = useMutation("add_team_members_to_team", addMembersToTeam, {
    onSuccess(response) {
      toast.success(response?.message);
      captureEvent("SET_TeamDetails_AddMembersSuccess", {
        team_id: data?.id,
        members_count: selectedIds?.length || 0,
      });
      handleModalClose();
      queryClient.invalidateQueries("view_team");
      if (typeof refetch === "function") {
        refetch();
      }
      formik.setSubmitting(false);
    },
    onError() {
      formik.setSubmitting(false);
    },
  });

  return (
    <>
      <div className="mb-1 inline-flex space-x-3">
        <div className="inline-flex justify-center border border-border rounded-[--radius] p-2 bg-background w-[50px]">
          <Image src="/images/icons/untitledui/group.svg" width="20" className="inline-block" />
        </div>
        <div>
          <h2 className="text-base font-semibold leading-7 ">Add team members</h2>
          <p className="text-paragraph">Please select members to add in this team.</p>
        </div>
      </div>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="py-3">
            <div className="mb-5">
              <label className="block mb-2 text-sm font-medium leading-6">Members</label>
              <div>
                <VirtualUserMultiSelect
                  selectedIds={selectedIds}
                  onChange={handleMembersChange}
                  disabled={addMembersToTeamFn.isLoading}
                  warningIds={Object.keys(conflictMembers)}
                  warningDetails={conflictMembers}
                  placeholder="Select users"
                  emptyMessage="No users found"
                  errorMessage="Error loading users"
                  workspaceId={workspaceId}
                  preloadedUsers={availableMembers}
                  disabledIds={disabledMemberIds}
                  teamUserSelect={true}
                />
                {/* {isCheckingTeamMemberConflicts && (
                  <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                    Checking selected members for conflicts...
                  </div>
                )} */}
                {hasConflicts && (
                  <div className="mt-2  text-warning-700">
                    Some users are part of other teams. Verify before proceeding.
                  </div>
                )}
                <FormError field="team_members" />
              </div>
            </div>
          </div>
          <div className="mt-0 space-x-2 flex">
            <Button
              className="flex-1"
              onClick={(e) => {
                e.preventDefault();
                handleModalClose();
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={formik.isSubmitting || !formik.isValid} className="flex-1">
              Add team members
              {formik.isSubmitting ? <div className="loader_circle ml-3"></div> : ""}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </>
  );
}
